import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ComponentType } from 'react';
import type {
  FlowObserverPanelState,
  FlowObserverPanelRuntimeRegistry,
  FlowObserverPanelTabDescriptor,
  FlowObserverPanelTabRuntimeState,
  FlowObserverTabContentProps,
  FlowObserverConstraints,
  PanelMode,
  TabEngineState,
  FloatingPanelGeometry,
} from '../types/panel.types.js';
import { migrateIfNeeded } from '../persistence/migration.js';
import { savePanelState, loadPanelState } from '../persistence/storage.js';
import type { WorkflowMode } from '../types/shared.types.js';

// ── Defaults ──────────────────────────────────────────────────────────────────

const BUFFER_SIZE_KEY = 'flowObserver.dataBufferMaxSize';
const DEFAULT_BUFFER_SIZE = 2000;

function getConfiguredBufferSize(): number {
  try {
    const stored = localStorage.getItem(BUFFER_SIZE_KEY);
    if (stored) {
      const size = parseInt(stored, 10);
      return Math.max(100, Math.min(size, 10000)); // Between 100-10000
    }
  } catch (e) {
    console.warn('Failed to read buffer size from localStorage', e);
  }
  return DEFAULT_BUFFER_SIZE;
}

const DEFAULT_CONSTRAINTS: FlowObserverConstraints = {
  dockedMinHeight: 150,
  dockedMaxHeightVh: 60,
  floatingMinWidth: 300,
  floatingMinHeight: 200,
  dataBufferMaxSize: getConfiguredBufferSize(),
};

const DEFAULT_FLOATING_GEOMETRY: FloatingPanelGeometry = {
  x: 100,
  y: 100,
  width: 600,
  height: 400,
  minimized: false,
};

function clampToViewport(geo: FloatingPanelGeometry): FloatingPanelGeometry {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    ...geo,
    x: Math.max(0, Math.min(geo.x, vw - geo.width)),
    y: Math.max(0, Math.min(geo.y, vh - geo.height)),
    width: Math.max(DEFAULT_CONSTRAINTS.floatingMinWidth, Math.min(geo.width, vw)),
    height: Math.max(DEFAULT_CONSTRAINTS.floatingMinHeight, Math.min(geo.height, vh)),
  };
}

function buildDefaultTabRuntimeState(id: string, order: number): FlowObserverPanelTabRuntimeState {
  return {
    id,
    engineState: 'available',
    userState: 'closed',
    loadState: 'idle',
    error: undefined,
    order,
    badge: undefined,
    dataBuffer: [],
  };
}

// ── Load initial state from localStorage ─────────────────────────────────────

migrateIfNeeded();
const persisted = loadPanelState();

const initialPanelMode: PanelMode = 'closed';
const initialDockedHeight: number = persisted?.dockedHeight ?? 240;

let initialFloatingGeometry: FloatingPanelGeometry = DEFAULT_FLOATING_GEOMETRY;
if (persisted?.panelMode === 'floating' && persisted?.floatingGeometry) {
  initialFloatingGeometry = clampToViewport(persisted.floatingGeometry);
}

const initialTabRuntimeStates: Record<string, FlowObserverPanelTabRuntimeState> = {};
if (persisted?.tabRuntimeStates) {
  for (const [id, saved] of Object.entries(persisted.tabRuntimeStates)) {
    initialTabRuntimeStates[id] = {
      id,
      engineState: 'available',
      userState: saved.userState ?? 'closed',
      loadState: 'idle',
      error: undefined,
      order: saved.order ?? 0,
      badge: undefined,
      dataBuffer: [],
    };
  }
}

const initialActiveTabId: string | null = persisted?.activeTabId ?? null;

// ── In-memory registry (never persisted) ─────────────────────────────────────

const registry: FlowObserverPanelRuntimeRegistry = {
  descriptors: {},
  contentComponents: {},
};

// Tracks current designer mode for showInModes enforcement. Starts at 'design'
// (the app's initial mode) and is kept in sync by the subscription below.
let _trackedDesignerMode: WorkflowMode = 'design';

// When all tabs are hidden due to a mode change, we auto-collapse the panel.
// This flag records that the collapse was automatic (not user-triggered) so we
// can restore the panel when tabs become visible again.
let _autoCollapsed = false;

// Tracks the last user-visible panel mode so re-opening restores to it instead
// of always defaulting to 'docked'. Seeded from persisted state on startup;
// first-time users get 'docked' (expanded) as the default.
let _lastActivePanelMode: PanelMode =
  (persisted?.panelMode === 'docked' ||
   persisted?.panelMode === 'floating' ||
   persisted?.panelMode === 'fullscreen')
    ? persisted.panelMode
    : 'docked';

// ── Store state shape ─────────────────────────────────────────────────────────

interface FlowObserverPanelStoreState extends FlowObserverPanelState {
  constraints: FlowObserverConstraints;
  /** Runtime-only (not persisted) — true once the user has manually picked a tab this session. See §5.1 nav-jump fix. */
  hasUserSelectedTab: boolean;
  markUserSelectedTab: () => void;

  configure: (c: Partial<FlowObserverConstraints>) => void;
  getRegistry: () => FlowObserverPanelRuntimeRegistry;

  setPanelMode: (mode: PanelMode) => void;
  setDockedHeight: (px: number) => void;
  setFloatingGeometry: (geo: Partial<FloatingPanelGeometry>) => void;
  setActiveTabId: (id: string | null) => void;

  registerTab: (
    descriptor: FlowObserverPanelTabDescriptor,
    component: ComponentType<FlowObserverTabContentProps>
  ) => void;
  unregisterTab: (id: string) => void;

  setTabEngineState: (id: string, state: TabEngineState) => void;
  setTabBadge: (id: string, badge: number | string | undefined) => void;
  activateTab: (id: string) => void;
  closeTab: (id: string) => void;
  openTab: (id: string) => void;
  /** Sets userState='open' without checking engineState. Use for init-time restoration
   *  when engineState may still be 'hidden' (mode not yet switched). */
  forceOpenTab: (id: string) => void;
  reorderTab: (id: string, newOrder: number) => void;

  pushData: <T = unknown>(id: string, data: T) => void;
  clearData: (id: string) => void;

  notifyUiStore: (mode: PanelMode) => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useFlowObserverPanelStore = create<FlowObserverPanelStoreState>()(
  devtools(
    (set, get) => ({
      panelMode: initialPanelMode,
      dockedHeight: initialDockedHeight,
      floatingGeometry: initialFloatingGeometry,
      activeTabId: initialActiveTabId,
      tabRuntimeStates: initialTabRuntimeStates,
      constraints: DEFAULT_CONSTRAINTS,
      hasUserSelectedTab: false,

      markUserSelectedTab: () => set({ hasUserSelectedTab: true }),

      configure: (c) => {
        set((state) => ({ constraints: { ...state.constraints, ...c } }));
      },

      getRegistry: () => registry,

      notifyUiStore: (mode) => {
        try {
          // Dynamic import to avoid circular dependency at module load time
          import('@flow-studio/store').then(({ useUIStore }) => {
            useUIStore.getState().setBottomPanelOpen(mode !== 'closed');
          }).catch(() => { /* store not available */ });
        } catch {
          // ignore
        }
      },

      setPanelMode: (mode) => {
        if (mode === 'docked' || mode === 'floating' || mode === 'fullscreen') {
          _lastActivePanelMode = mode;
        }
        set({ panelMode: mode });
        get().notifyUiStore(mode);
        savePanelState({ ...get(), panelMode: mode });
      },

      setDockedHeight: (px) => {
        const { constraints } = get();
        const maxPx = (window.innerHeight * constraints.dockedMaxHeightVh) / 100;
        const clamped = Math.max(constraints.dockedMinHeight, Math.min(px, maxPx));
        set({ dockedHeight: clamped });
        savePanelState({ ...get(), dockedHeight: clamped });
      },

      setFloatingGeometry: (geo) => {
        const current = get().floatingGeometry;
        const merged = clampToViewport({ ...current, ...geo });
        set({ floatingGeometry: merged });
        savePanelState({ ...get(), floatingGeometry: merged });
      },

      setActiveTabId: (id) => {
        set({ activeTabId: id });
        savePanelState({ ...get(), activeTabId: id });
      },

      registerTab: (descriptor, component) => {
        const { tabRuntimeStates, constraints } = get();
        console.log('[Store.registerTab] Registering tab:', { id: descriptor.id, timestamp: new Date().toISOString() });

        if (registry.descriptors[descriptor.id]) {
          console.warn(`[FlowObserverPanelStore] registerTab: replacing "${descriptor.id}"`);
        }

        registry.descriptors[descriptor.id] = descriptor;
        registry.contentComponents[descriptor.id] = component;

        // Compute next order for new tabs
        const existingOrders = Object.values(tabRuntimeStates).map((t) => t.order);
        const nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 0;

        const existing = tabRuntimeStates[descriptor.id];

        let runtimeState: FlowObserverPanelTabRuntimeState;

        // If the current designer mode excludes this tab's showInModes, start it hidden.
        // _trackedDesignerMode is kept in sync by the useDesignerModeStore subscription below.
        const effectiveEngineState: TabEngineState =
          descriptor.showInModes.length > 0 && !descriptor.showInModes.includes(_trackedDesignerMode)
            ? 'hidden'
            : descriptor.engineState;

        if (existing) {
          // Merge: engine state from descriptor (or hidden per mode), user state from localStorage.
          // IMPORTANT: only force userState='closed' when the DESCRIPTOR itself marks this tab
          // as hidden or disabled. If it's hidden only because the current designer mode doesn't
          // include it (mode mismatch), preserve the user's persisted preference — the mode
          // subscription will restore engineState to 'available' when the mode switches, at which
          // point the tab will appear open without any extra action.
          let engineState = effectiveEngineState;
          let userState = existing.userState;

          const hiddenByDescriptor = descriptor.engineState === 'hidden';
          const disabledByDescriptor = descriptor.engineState === 'disabled';
          if (hiddenByDescriptor || disabledByDescriptor) {
            userState = 'closed';
          }

          runtimeState = {
            ...existing,
            engineState,
            userState,
            loadState: 'idle',
            error: undefined,
            badge: undefined,
            dataBuffer: [],
          };
        } else {
          runtimeState = {
            ...buildDefaultTabRuntimeState(descriptor.id, nextOrder),
            engineState: effectiveEngineState,
          };
        }

        const nextTabRuntimeStates = {
          ...tabRuntimeStates,
          [descriptor.id]: runtimeState,
        };

        // Validate activeTabId
        let { activeTabId } = get();
        if (activeTabId && activeTabId === descriptor.id) {
          const rt = nextTabRuntimeStates[activeTabId];
          if (!rt || rt.engineState !== 'available' || rt.userState !== 'open') {
            activeTabId = null;
          }
        }

        set({ tabRuntimeStates: nextTabRuntimeStates, activeTabId });
        savePanelState({ ...get(), tabRuntimeStates: nextTabRuntimeStates, activeTabId });

        // Remove tabs from localStorage that no longer exist in registry
        // (handled implicitly since we only serialize registered tabs)
        const maxBufSize = constraints.dataBufferMaxSize;
        // Ensure buffer respects max size
        if (runtimeState.dataBuffer.length > maxBufSize) {
          set((state) => ({
            tabRuntimeStates: {
              ...state.tabRuntimeStates,
              [descriptor.id]: {
                ...state.tabRuntimeStates[descriptor.id],
                dataBuffer: state.tabRuntimeStates[descriptor.id].dataBuffer.slice(-maxBufSize),
              },
            },
          }));
        }
      },

      unregisterTab: (id) => {
        const { tabRuntimeStates, activeTabId } = get();
        const descriptor = registry.descriptors[id];
        const runtime = tabRuntimeStates[id];

        if (!descriptor) return;

        if (runtime?.userState === 'open') {
          descriptor.onUnregister?.();
        }

        delete registry.descriptors[id];
        delete registry.contentComponents[id];

        const next = { ...tabRuntimeStates };
        delete next[id];

        let newActiveTabId = activeTabId;
        if (activeTabId === id) {
          const openTabs = Object.values(next)
            .filter((t) => t.userState === 'open' && t.engineState === 'available')
            .sort((a, b) => a.order - b.order);
          newActiveTabId = openTabs[0]?.id ?? null;
        }

        set({ tabRuntimeStates: next, activeTabId: newActiveTabId });
        savePanelState({ ...get(), tabRuntimeStates: next, activeTabId: newActiveTabId });
      },

      setTabEngineState: (id, state) => {
        const { tabRuntimeStates, activeTabId } = get();
        console.log('[Store.setTabEngineState] Setting tab state:', { id, newState: state, timestamp: new Date().toISOString() });
        if (!tabRuntimeStates[id]) {
          console.warn(`[FlowObserverPanelStore] setTabEngineState: unknown tab "${id}"`);
          return;
        }

        const updatedStates = {
          ...tabRuntimeStates,
          [id]: { ...tabRuntimeStates[id], engineState: state },
        };

        // If hidden/disabled and was active, switch to next tab
        let newActiveTabId = activeTabId;
        if ((state === 'hidden' || state === 'disabled') && activeTabId === id) {
          const openTabs = Object.entries(updatedStates)
            .filter(([tid, t]) => tid !== id && t.userState === 'open' && t.engineState === 'available')
            .sort(([, a], [, b]) => a.order - b.order);
          newActiveTabId = openTabs[0]?.[0] ?? null;
        }

        set({ tabRuntimeStates: updatedStates, activeTabId: newActiveTabId });
      },

      setTabBadge: (id, badge) => {
        const { tabRuntimeStates } = get();
        if (!tabRuntimeStates[id]) return;
        set((state) => ({
          tabRuntimeStates: {
            ...state.tabRuntimeStates,
            [id]: { ...state.tabRuntimeStates[id], badge },
          },
        }));
      },

      activateTab: (id) => {
        const { tabRuntimeStates, panelMode } = get();
        const runtime = tabRuntimeStates[id];
        const descriptor = registry.descriptors[id];
        console.log('[Store.activateTab] Activating tab:', { id, hasRuntime: !!runtime, hasDescriptor: !!descriptor, engineState: runtime?.engineState, timestamp: new Date().toISOString() });

        if (!descriptor || !runtime) {
          console.warn('[Store.activateTab] Missing descriptor or runtime:', { id, descriptor: !!descriptor, runtime: !!runtime });
          return;
        }
        if (runtime.engineState === 'hidden' || runtime.engineState === 'disabled') {
          console.warn(`[FlowObserverPanelStore] activateTab: tab "${id}" is ${runtime.engineState}`);
          return;
        }

        // Open panel if closed or collapsed — restore to last known visible mode
        let newMode = panelMode;
        if (panelMode === 'closed') newMode = _lastActivePanelMode;
        else if (panelMode === 'collapsed') newMode = _lastActivePanelMode;

        // Open tab if not already open
        const wasOpen = runtime.userState === 'open';
        const updatedStates = {
          ...tabRuntimeStates,
          [id]: {
            ...runtime,
            userState: 'open' as const,
            // Only enter loading state when the tab is being opened for the first time (or after close).
            // If already open, keep the current loadState so content stays visible.
            ...(wasOpen ? {} : { loadState: 'loading' as const }),
          },
        };

        set({ tabRuntimeStates: updatedStates, activeTabId: id, panelMode: newMode });
        get().notifyUiStore(newMode);
        savePanelState({ ...get(), tabRuntimeStates: updatedStates, activeTabId: id, panelMode: newMode });

        if (!wasOpen) {
          descriptor.onActivate()
            .then(() => {
              set((state) => ({
                tabRuntimeStates: {
                  ...state.tabRuntimeStates,
                  [id]: { ...state.tabRuntimeStates[id], loadState: 'loaded' },
                },
              }));
            })
            .catch((err: unknown) => {
              const message = err instanceof Error ? err.message : String(err);
              set((state) => ({
                tabRuntimeStates: {
                  ...state.tabRuntimeStates,
                  [id]: {
                    ...state.tabRuntimeStates[id],
                    loadState: 'error',
                    error: { message, retryable: true },
                  },
                },
              }));
            });
        }
      },

      closeTab: (id) => {
        const { tabRuntimeStates, activeTabId } = get();
        const runtime = tabRuntimeStates[id];
        const descriptor = registry.descriptors[id];
        if (!runtime) return;

        if (activeTabId === id) {
          descriptor?.onDeactivate?.();
        }
        descriptor?.onClose?.();

        const updatedStates = {
          ...tabRuntimeStates,
          [id]: {
            ...runtime,
            userState: 'closed' as const,
            loadState: 'idle' as const,
            error: undefined,
            badge: undefined,
            dataBuffer: [],
          },
        };

        let newActiveTabId = activeTabId;
        if (activeTabId === id) {
          const openTabs = Object.entries(updatedStates)
            .filter(([tid, t]) => tid !== id && t.userState === 'open' && t.engineState === 'available')
            .sort(([, a], [, b]) => a.order - b.order);
          newActiveTabId = openTabs[0]?.[0] ?? null;
        }

        set({ tabRuntimeStates: updatedStates, activeTabId: newActiveTabId });
        savePanelState({ ...get(), tabRuntimeStates: updatedStates, activeTabId: newActiveTabId });
      },

      openTab: (id) => {
        get().activateTab(id);
      },

      forceOpenTab: (id) => {
        const { tabRuntimeStates } = get();
        if (!tabRuntimeStates[id]) return;
        const updated = {
          ...tabRuntimeStates,
          [id]: { ...tabRuntimeStates[id], userState: 'open' as const },
        };
        set({ tabRuntimeStates: updated });
        savePanelState({ ...get(), tabRuntimeStates: updated });
      },

      reorderTab: (id, newOrder) => {
        const { tabRuntimeStates } = get();
        if (!tabRuntimeStates[id]) return;
        const updated = {
          ...tabRuntimeStates,
          [id]: { ...tabRuntimeStates[id], order: newOrder },
        };
        set({ tabRuntimeStates: updated });
        savePanelState({ ...get(), tabRuntimeStates: updated });
      },

      pushData: (id, data) => {
        const { tabRuntimeStates, constraints } = get();
        if (!tabRuntimeStates[id]) {
          return;
        }
        const current = tabRuntimeStates[id].dataBuffer;

        // execution-logs is fed by two independent SignalR sources for the same node
        // activity — live lifecycle progress events and the per-node activity replay —
        // which can emit matching log lines for the same moment. Drop the second one
        // instead of showing the same node/message/second twice in a row.
        if (id === 'execution-logs') {
          const entry = data as { sourceNodeId?: string; message?: string; timestamp?: string };
          const dedupKey = `${entry.sourceNodeId ?? ''}|${entry.message ?? ''}|${(entry.timestamp ?? '').slice(0, 19)}`;
          const isDuplicate = current.slice(-25).some((e) => {
            const existing = e as { sourceNodeId?: string; message?: string; timestamp?: string };
            const existingKey = `${existing.sourceNodeId ?? ''}|${existing.message ?? ''}|${(existing.timestamp ?? '').slice(0, 19)}`;
            return existingKey === dedupKey;
          });
          if (isDuplicate) return;
        }

        const maxSize = constraints.dataBufferMaxSize;
        const next = current.length >= maxSize
          ? [...current.slice(1), data]
          : [...current, data];
        set((state) => ({
          tabRuntimeStates: {
            ...state.tabRuntimeStates,
            [id]: { ...state.tabRuntimeStates[id], dataBuffer: next },
          },
        }));
      },

      clearData: (id) => {
        const { tabRuntimeStates } = get();
        if (!tabRuntimeStates[id]) return;
        set((state) => ({
          tabRuntimeStates: {
            ...state.tabRuntimeStates,
            [id]: { ...state.tabRuntimeStates[id], dataBuffer: [] },
          },
        }));
      },
    }),
    { name: 'FlowObserverPanelStore' }
  )
);

// Push persisted panel mode to uiStore on startup
try {
  import('@flow-studio/store').then(({ useUIStore }) => {
    useUIStore.getState().setBottomPanelOpen(initialPanelMode !== 'closed');
  }).catch(() => { /* store not available yet */ });
} catch {
  // ignore
}

// Subscribe to execution ID changes and clear stale execution-logs / log-detail
// buffers the instant a NEW execution starts. This is the single authoritative
// trigger point — independent of which UI action started the execution, and
// decoupled from the SignalR "first thread" heuristic in useFlowObserverSignalR,
// which can race with straggling events from the previous run and leave old and
// new execution logs mixed together in the same buffer.
try {
  import('@flow-studio/store').then(({ useDesignerModeStore }) => {
    useDesignerModeStore.subscribe(
      (
        state: { currentExecutionId: { ResID?: string } | null },
        prevState: { currentExecutionId: { ResID?: string } | null }
      ) => {
        const prevResID = prevState?.currentExecutionId?.ResID;
        const nextResID = state?.currentExecutionId?.ResID;
        if (nextResID && nextResID !== prevResID) {
          const panelStore = useFlowObserverPanelStore.getState();
          panelStore.clearData('execution-logs');
          panelStore.clearData('log-detail');
        }
      }
    );
  }).catch(() => { /* store not available */ });
} catch {
  // ignore
}

// Subscribe to designer mode changes and enforce showInModes on all registered tabs.
// When mode changes, tabs whose showInModes excludes the new mode are hidden;
// tabs that become eligible are restored to their descriptor's engineState.
// Side-effect: auto-collapse the panel when all tabs are hidden, auto-restore when
// any tab becomes visible again (only if we triggered the collapse ourselves).
try {
  import('@flow-studio/store').then(({ useDesignerModeStore }) => {
    useDesignerModeStore.subscribe((state: { mode: string }) => {
      const mode = state.mode as WorkflowMode;
      _trackedDesignerMode = mode;

      const store = useFlowObserverPanelStore.getState();
      const { tabRuntimeStates } = store;

      for (const [id, runtime] of Object.entries(tabRuntimeStates)) {
        const descriptor = registry.descriptors[id];
        if (!descriptor || descriptor.showInModes.length === 0) continue;

        const shouldBeVisible = descriptor.showInModes.includes(mode);
        if (shouldBeVisible && runtime.engineState === 'hidden') {
          store.setTabEngineState(id, descriptor.engineState);
        } else if (!shouldBeVisible && runtime.engineState !== 'hidden') {
          store.setTabEngineState(id, 'hidden');
        }
      }

      // After updating tab visibility, check whether any tab is now non-hidden.
      const updatedStates = useFlowObserverPanelStore.getState().tabRuntimeStates;
      const hasVisibleTab = Object.values(updatedStates).some(
        (t) => t.engineState !== 'hidden'
      );
      const { panelMode } = useFlowObserverPanelStore.getState();

      if (!hasVisibleTab && (panelMode === 'docked' || panelMode === 'fullscreen')) {
        // All tabs hidden — collapse to a thin strip so the canvas isn't obscured.
        _autoCollapsed = true;
        useFlowObserverPanelStore.getState().setPanelMode('collapsed');
      } else if (hasVisibleTab && panelMode === 'collapsed' && _autoCollapsed) {
        // Tabs visible again and we were the ones who collapsed — restore to last visible mode.
        _autoCollapsed = false;
        useFlowObserverPanelStore.getState().setPanelMode(_lastActivePanelMode);
      }
    });
  }).catch(() => { /* store not available */ });
} catch {
  // ignore
}
